import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, Divider, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useShallow } from "zustand/react/shallow";

import styles from "../../../assets/styles/componentStyles/billingStyles/billingAddOns.module.scss";

import { useGenericStore } from "../../../store/genericStore";
import { useBillingStore } from "../../../store/billingStore";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import { thousandSeparator } from "../../../utils/constants";
import { getChangePlanAddOnDescriptionText, isQuantityBasedAddOn, isRemoveBrandingAddOn, isAiCreditsAddOn, filterAddOnsByTabs } from "../utils.billing";
import { getAddOnsResponse, subscribePlanAddOn } from "../../../models/axiosModel";
import PrimaryIOSSwitch from "../../utility/switch/primaryIOSSwitch";
import { primaryColor } from "../../../utils/colors";

interface AddOnsProps {
    onAddOnsChange: (addOns: subscribePlanAddOn[]) => void;
    initialAddOns?: subscribePlanAddOn[] | null;
}

type AddOnState = {
    quantity: number;
    recurring: boolean;
};

const DEFAULT_STATE: AddOnState = { quantity: 0, recurring: false };

const getFlagRecurring = (addOn: getAddOnsResponse, quantity: number, recurring: boolean) => {
    if (isRemoveBrandingAddOn(addOn)) return true;
    if (isQuantityBasedAddOn(addOn)) return quantity >= 1 && recurring;

    return addOn.flag_recurring;
};


const AddOns = ({ onAddOnsChange, initialAddOns = null }: AddOnsProps) => {
    
    const { setFlagLoading } = useContext(UserContext) as UserContextPayload;

    const { showError } = useGenericStore(useShallow((state) => ({
        showError: state.showError,
    })));

    const { billingAddOns, getBillingAddOnsApi, billingCycle, billingCurrency, billingCurrencySymbol, billingPriceSuffix } = useBillingStore(useShallow((state) => ({
        billingAddOns: state.billingAddOns,
        getBillingAddOnsApi: state.getBillingAddOnsApi,
        billingCycle: state.billingCycle,
        billingCurrency: state.billingCurrency,
        billingCurrencySymbol: state.billingCurrencySymbol,
        billingPriceSuffix: state.billingPriceSuffix,
    })));

    const [addOnState, setAddOnState] = useState<Record<string, AddOnState>>({});

    const initialAppliedRef = useRef(false);

    // Fetch Add-ons
    const fetchAddOns = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        
        const res = await getBillingAddOnsApi();
        
        if (!res?.status) {
            showError(res?.error?.message || "Something went wrong");
        }
        
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    };
    
    // Functions
    const filteredAddOns = useMemo(
        () => filterAddOnsByTabs(billingAddOns, billingCycle, billingCurrency),
        [billingAddOns, billingCycle, billingCurrency]
    );

    const getState = (name: string): AddOnState => addOnState[name] ?? DEFAULT_STATE;

    const setQuantity = (name: string, quantity: number) => {
        setAddOnState((prev) => ({
            ...prev,
            [name]: {
                quantity: Math.max(0, quantity),
                recurring: prev[name]?.recurring ?? false,
            },
        }));
    };

    const setRecurring = (name: string, recurring: boolean) => {
        setAddOnState((prev) => ({
            ...prev,
            [name]: {
                quantity: prev[name]?.quantity ?? 0,
                recurring,
            },
        }));
    };

    useEffect(() => {
        if(!billingAddOns) {
            fetchAddOns();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply query params once add-ons are loaded
    useEffect(() => {
        if (initialAppliedRef.current || !initialAddOns?.length || !billingAddOns?.length || !filteredAddOns.length) {
            return;
        }

        const next: Record<string, AddOnState> = {};

        initialAddOns.forEach((item) => {
            const source = billingAddOns.find((addOn) => String(addOn.billing_add_on_id) === String(item.billing_add_on_id));
            const matched = source
                ? filteredAddOns.find((addOn) => addOn.billing_add_on_name === source.billing_add_on_name)
                : filteredAddOns.find((addOn) => String(addOn.billing_add_on_id) === String(item.billing_add_on_id));

            if (!matched) return;

            next[matched.billing_add_on_name] = {
                quantity: Math.max(item.quantity, 1),
                recurring: isRemoveBrandingAddOn(matched) ? true : item.flag_recurring,
            };
        });

        if (!Object.keys(next).length) return;

        initialAppliedRef.current = true;
        setAddOnState(next);
    }, [initialAddOns, billingAddOns, filteredAddOns]);

    // Handle Add-ons Change
    useEffect(() => {
        const payload = filteredAddOns.reduce<subscribePlanAddOn[]>((acc, addOn) => {
            const { quantity, recurring } = getState(addOn.billing_add_on_name);
            
            if (quantity <= 0) return acc;

            acc.push({
                billing_add_on_id: addOn.billing_add_on_id,
                quantity,
                flag_recurring: getFlagRecurring(addOn, quantity, recurring),
            });

            return acc;
        }, []);

        onAddOnsChange(payload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addOnState, filteredAddOns, onAddOnsChange]);

    if (!filteredAddOns.length) return null;

    return (
        <Box className={styles.addOnsSection}>
            <Typography className={styles.addOnsSectionLabel}>
                Add-ons
            </Typography>

            <Box className={styles.addOnsList}>
                {filteredAddOns.map((addOn) => {
                    const name = addOn.billing_add_on_name;
                    const { quantity, recurring } = getState(name);
                    const isQuantityBased = isQuantityBasedAddOn(addOn);
                    const isRemoveBranding = isRemoveBrandingAddOn(addOn);
                    const isSelected = quantity > 0;

                    const displayAmount = isQuantityBased
                        ? addOn.billing_add_on_amount * Math.max(quantity, 1)
                        : addOn.billing_add_on_amount;

                    const priceSuffix = isAiCreditsAddOn(addOn)
                        ? ((quantity > 0 && recurring) ? "" : "one-time")
                        : billingPriceSuffix;

                    const showRecurringSwitch = isQuantityBased && quantity > 0;

                    return (
                        <Box
                            className={`${styles.addOnCard} ${isSelected ? styles.addOnCardSelected : ""} ${!isQuantityBased ? styles.addOnCardClickable : ""}`}
                            key={addOn.billing_add_on_id}
                            onClick={!isQuantityBased ? () => setQuantity(name, isSelected ? 0 : 1) : undefined}
                            role={!isQuantityBased ? "button" : undefined}
                            tabIndex={!isQuantityBased ? 0 : undefined}
                            onKeyDown={!isQuantityBased ? (event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    setQuantity(name, isSelected ? 0 : 1);
                                }
                            } : undefined}
                        >
                            <Box className={styles.addOnMainRow}>
                                <Box className={styles.addOnLeft}>
                                    {!isQuantityBased && (
                                        <Checkbox
                                            className={styles.addOnCheckbox}
                                            checked={isSelected}
                                            onChange={(event) => setQuantity(name, event.target.checked ? 1 : 0)}
                                            onClick={(event) => event.stopPropagation()}
                                            size="small"
                                        />
                                    )}

                                    <Box className={styles.addOnContent}>
                                        <Typography className={styles.addOnName}>
                                            {addOn.billing_add_on_name}
                                        </Typography>

                                        <Typography className={styles.addOnDescription}>
                                            {getChangePlanAddOnDescriptionText(addOn, quantity, recurring)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box className={styles.addOnRight}>
                                    {isQuantityBased && (
                                        <Box className={styles.quantityControl}>
                                            <IconButton
                                                className={styles.quantityBtn}
                                                onClick={() => setQuantity(name, quantity - 1)}
                                                disabled={quantity <= 0}
                                                size="small"
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>

                                            <Typography className={styles.quantityValue}>
                                                {quantity}
                                            </Typography>

                                            <IconButton
                                                className={styles.quantityBtn}
                                                onClick={() => setQuantity(name, quantity + 1)}
                                                size="small"
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}

                                    <Box className={styles.addOnPriceWrap}>
                                        <Typography className={styles.addOnPrice}>
                                            {billingCurrencySymbol}{thousandSeparator(displayAmount)}
                                        </Typography>

                                        <Typography className={styles.addOnPriceSuffix}>
                                            {priceSuffix}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {showRecurringSwitch && (
                                <>
                                    <Divider className={styles.addOnDivider} />

                                    <Box className={styles.recurringRow}>
                                        <Typography className={styles.recurringText}>
                                            Recurring purchase • auto-renews with your plan
                                        </Typography>

                                        <PrimaryIOSSwitch
                                            checked={isRemoveBranding ? true : recurring}
                                            disabled={isRemoveBranding}
                                            onChange={(event) => {
                                                if (isRemoveBranding) return;
                                                setRecurring(name, event.target.checked);
                                            }}
                                            sx={isRemoveBranding ? {
                                                opacity: 1,
                                                "& .MuiSwitch-switchBase.Mui-disabled": { color: "#fff" },
                                                "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
                                                    backgroundColor: primaryColor,
                                                    opacity: 1,
                                                },
                                            } : undefined}
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default AddOns;